import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { Award, Check, Coins, Download, Gem, ImageIcon, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { appraisalAPI } from '../lib/api';

const METAL_OPTIONS = [
  { key: '10k', label: '10K Gold' },
  { key: '14k', label: '14K Gold' },
  { key: '18k', label: '18K Gold' },
  { key: 'platinum', label: 'Platinum' },
  { key: 'silver', label: 'Silver' },
];

const SHAPE_OPTIONS = [
  'Round Brilliant', 'Oval Brilliant', 'Princess', 'Emerald', 'Cushion',
  'Pear', 'Marquise', 'Radiant', 'Asscher', 'Heart',
];
const COLOR_OPTIONS = ['D', 'E', 'F', 'G', 'H', 'I', 'J', 'K'];
const CLARITY_OPTIONS = ['IF', 'VVS1', 'VVS2', 'VS1', 'VS2', 'SI1', 'SI2'];
const GRADE_OPTIONS = ['Excellent', 'Very Good', 'Good', 'Fair'];
const FLUORESCENCE_OPTIONS = ['None', 'Faint', 'Medium', 'Strong', 'Very Strong'];

const fmtUSD = (n) => (n == null ? '—' : `$${Math.round(n).toLocaleString()}`);

export default function AppraisalPanel({ product }) {
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [refreshingGold, setRefreshingGold] = useState(false);
  const [state, setState] = useState(null);   // full server response

  const [metalKey, setMetalKey] = useState('14k');
  const [centerCarat, setCenterCarat] = useState(3.0);
  const [ringWeightG, setRingWeightG] = useState(5.0);
  const [isSpecialty, setIsSpecialty] = useState(false);
  const [shape, setShape] = useState('Round Brilliant');
  const [color, setColor] = useState('E');
  const [clarity, setClarity] = useState('VVS2');
  const [cut, setCut] = useState('Excellent');
  const [polish, setPolish] = useState('Excellent');
  const [symmetry, setSymmetry] = useState('Excellent');
  const [fluorescence, setFluorescence] = useState('None');
  const [igiNumber, setIgiNumber] = useState('');
  const [availableImages, setAvailableImages] = useState([]);
  const [selectedImageUrls, setSelectedImageUrls] = useState([]);

  const MAX_IMAGES = 4;

  const toggleImage = (url) => {
    setSelectedImageUrls((prev) => {
      if (prev.includes(url)) return prev.filter((u) => u !== url);
      if (prev.length >= MAX_IMAGES) {
        toast.info(`Up to ${MAX_IMAGES} images. Deselect one to pick another.`);
        return prev;
      }
      return [...prev, url];
    });
  };

  const hydrate = (data) => {
    setState(data);
    const saved = data.saved || {};
    const grading = data.grading || {};
    setMetalKey(saved.metal_key || '14k');
    setCenterCarat(saved.center_carat ?? 3.0);
    setRingWeightG(saved.ring_weight_g ?? (data.preview?.ring_weight_g || 5.0));
    setIsSpecialty(saved.is_specialty_cut ?? !!product?.is_specialty_cut);
    setShape(grading.shape || 'Round Brilliant');
    setColor(grading.color || 'E');
    setClarity(grading.clarity || 'VVS2');
    setCut(grading.cut || 'Excellent');
    setPolish(grading.polish || 'Excellent');
    setSymmetry(grading.symmetry || 'Excellent');
    setFluorescence(grading.fluorescence || 'None');
    setIgiNumber(grading.igi_report_number || grading.gia_report_number || '');
    const imgs = Array.isArray(data.available_images) ? data.available_images : [];
    setAvailableImages(imgs);
    const availUrls = new Set(imgs.map((i) => i.url));
    // Restore saved selection (filter to URLs that still exist), else seed
    // with the first available image so generation never fails silently.
    const saved_sel = Array.isArray(data.selected_image_urls) ? data.selected_image_urls : [];
    const cleaned = saved_sel.filter((u) => availUrls.has(u)).slice(0, MAX_IMAGES);
    if (cleaned.length === 0 && imgs.length > 0) {
      setSelectedImageUrls([imgs[0].url]);
    } else {
      setSelectedImageUrls(cleaned);
    }
  };

  useEffect(() => {
    if (!product?.id) return;
    setLoading(true);
    appraisalAPI.get(product.id)
      .then((r) => hydrate(r.data))
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load appraisal'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [product?.id]);

  const refreshGold = async () => {
    try {
      setRefreshingGold(true);
      const r = await appraisalAPI.goldPrice();
      toast.success(`Spot gold: $${(r.data.spot_per_oz || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}/oz`);
      // Re-pull the appraisal preview so the value reflects the fresh gold price.
      const data = await appraisalAPI.get(product.id);
      hydrate(data.data);
    } catch (e) {
      toast.error('Could not refresh gold price');
    } finally {
      setRefreshingGold(false);
    }
  };

  const save = async () => {
    try {
      const r = await appraisalAPI.update(product.id, {
        metal_key: metalKey,
        center_carat: Number(centerCarat) || 3.0,
        ring_weight_g: Number(ringWeightG) || 5.0,
        is_specialty_cut: isSpecialty,
        grading: {
          shape, color, clarity, cut, polish, symmetry, fluorescence,
          igi_report_number: igiNumber.trim(),
        },
      });
      hydrate(r.data);
      toast.success('Appraisal saved');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const generate = async () => {
    try {
      setGenerating(true);
      try {
        await save();
      } catch (saveErr) {
        // save() already toasts its own error — keep going so the user
        // can still hit Generate if the input save was a transient fail.
        console.warn('appraisal save failed before generate:', saveErr);
      }
      const r = await appraisalAPI.generate(product.id, 'both', selectedImageUrls);
      toast.success(`Appraisal value $${(r.data.appraisal_value_usd || 0).toLocaleString()}`);
      try {
        const refreshed = await appraisalAPI.get(product.id);
        hydrate(refreshed.data);
      } catch (refreshErr) {
        // Refresh isn't critical — PDFs are already generated and saved.
        console.warn('appraisal post-gen refresh failed:', refreshErr);
      }
    } catch (e) {
      // Surface the actual backend detail / status when available.
      const detail = e.response?.data?.detail;
      const status = e.response?.status;
      const msg = detail
        ? `Generation failed: ${detail}`
        : status
          ? `Generation failed (${status}) — open the network tab for details`
          : `Generation failed: ${e.message || 'network error'}`;
      console.error('appraisal generate failed:', e);
      toast.error(msg);
    } finally {
      setGenerating(false);
    }
  };

  const preview = state?.preview;
  const saved = state?.saved || {};
  const components = preview?.components || {};
  const spotPerOz = preview?.spot_gold_per_oz;
  const spotPerGram = useMemo(
    () => (spotPerOz ? spotPerOz / 31.1034768 : null),
    [spotPerOz],
  );

  return (
    <div className="bg-white border border-zinc-200 rounded-xl p-5 space-y-4" data-testid="appraisal-panel">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-base font-semibold text-zinc-900 inline-flex items-center gap-2">
            <Award size={15} className="text-amber-600" /> Appraisal Certificate
          </h2>
          <p className="text-xs text-zinc-500 mt-0.5">
            Spot-gold-based replacement value · single canonical figure per product · internal-only.
          </p>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={refreshGold}
          disabled={refreshingGold}
          data-testid="appraisal-refresh-gold-btn"
        >
          {refreshingGold
            ? <Loader2 size={12} className="mr-1.5 animate-spin" />
            : <RefreshCw size={12} className="mr-1.5" />}
          Refresh gold price
        </Button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-10 text-zinc-400">
          <Loader2 size={16} className="animate-spin mr-2" /> Loading…
        </div>
      ) : (
        <>
          {/* spot strip */}
          <div className="grid grid-cols-3 gap-3 bg-amber-50/50 border border-amber-100 rounded-lg p-3" data-testid="appraisal-spot-strip">
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 inline-flex items-center gap-1">
                <Coins size={11} /> Spot gold (live)
              </div>
              <div className="text-lg font-semibold text-zinc-900 mt-1 font-mono">
                {spotPerOz ? `$${spotPerOz.toLocaleString(undefined, { maximumFractionDigits: 2 })}/oz` : '—'}
              </div>
              <div className="text-[10px] text-zinc-500 font-mono mt-0.5">
                {spotPerGram ? `$${spotPerGram.toFixed(2)}/g pure` : ''}
              </div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 inline-flex items-center gap-1">
                <Gem size={11} /> Production value
              </div>
              <div className="text-lg font-semibold text-zinc-900 mt-1 font-mono">{fmtUSD(preview?.subtotal)}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">Metal + stones + setting + profit</div>
            </div>
            <div>
              <div className="text-[10px] uppercase tracking-wider font-semibold text-amber-700 inline-flex items-center gap-1">
                <Sparkles size={11} /> Appraisal value
              </div>
              <div className="text-2xl font-bold text-amber-700 mt-1 font-mono">{fmtUSD(preview?.total_usd)}</div>
              <div className="text-[10px] text-zinc-500 mt-0.5">
                + {preview ? Math.round((preview.uplift_pct || 0) * 100) : 50}% retail uplift, no fees/shipping
              </div>
            </div>
          </div>

          {/* image selector — pick up to 4 product photos for the certificate collage */}
          <div className="bg-white border border-zinc-200 rounded-lg p-3" data-testid="appraisal-image-selector">
            <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
              <div>
                <div className="text-xs font-semibold text-zinc-700 inline-flex items-center gap-1.5">
                  <ImageIcon size={13} className="text-zinc-500" /> Pick photos for the certificate
                </div>
                <div className="text-[10px] text-zinc-500 mt-0.5">
                  Up to {MAX_IMAGES} images — rendered as a collage on the 1-pager and panel 1 of the booklet.
                </div>
              </div>
              <span
                className="text-[11px] font-mono px-2 py-1 rounded bg-amber-50 text-amber-700 border border-amber-100"
                data-testid="appraisal-image-count"
              >
                {selectedImageUrls.length}/{MAX_IMAGES} selected
              </span>
            </div>
            {availableImages.length === 0 ? (
              <p className="text-[11px] text-zinc-400 italic px-1 py-3">
                No product images found. Upload media to this listing to enable the collage.
              </p>
            ) : (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-6 gap-2" data-testid="appraisal-image-grid">
                {availableImages.map((img) => {
                  const selectedIdx = selectedImageUrls.indexOf(img.url);
                  const isSel = selectedIdx >= 0;
                  return (
                    <button
                      key={img.id || img.url}
                      type="button"
                      onClick={() => toggleImage(img.url)}
                      className={`relative aspect-square rounded overflow-hidden border-2 transition focus:outline-none ${
                        isSel
                          ? 'border-amber-500 ring-2 ring-amber-200'
                          : 'border-zinc-200 hover:border-zinc-400'
                      }`}
                      title={img.label || ''}
                      data-testid={`appraisal-image-thumb-${img.id || img.url}`}
                    >
                      <img
                        src={img.url}
                        alt={img.label || 'product'}
                        className="w-full h-full object-cover bg-zinc-100"
                        loading="lazy"
                      />
                      {isSel && (
                        <span className="absolute top-1 right-1 bg-amber-500 text-white text-[10px] font-bold w-5 h-5 rounded-full inline-flex items-center justify-center">
                          {selectedIdx + 1}
                        </span>
                      )}
                      {isSel && (
                        <span className="absolute bottom-1 left-1 bg-white/95 text-amber-600 rounded-full p-0.5">
                          <Check size={11} />
                        </span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* inputs — left = setting, right = diamond */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
            {/* Setting */}
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Setting</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-zinc-600">Metal</Label>
                  <select
                    value={metalKey}
                    onChange={(e) => setMetalKey(e.target.value)}
                    className="w-full h-9 mt-1 px-2 text-sm border border-zinc-200 rounded bg-white"
                    data-testid="appraisal-metal-select"
                  >
                    {METAL_OPTIONS.map((m) => (
                      <option key={m.key} value={m.key}>{m.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Ring weight (g)</Label>
                  <Input
                    type="number" step="0.1" min="0.5" max="50"
                    value={ringWeightG}
                    onChange={(e) => setRingWeightG(e.target.value)}
                    className="h-9 mt-1"
                    data-testid="appraisal-weight-input"
                  />
                </div>
              </div>
              <div className="flex items-center justify-between bg-zinc-50 border border-zinc-200 rounded p-2.5">
                <div>
                  <div className="text-xs font-medium text-zinc-700">Specialty cut</div>
                  <div className="text-[10px] text-zinc-500">Premium center-stone shapes ($250/ct vs $150/ct)</div>
                </div>
                <Switch checked={isSpecialty} onCheckedChange={setIsSpecialty} data-testid="appraisal-specialty-toggle" />
              </div>
            </div>

            {/* Diamond grading */}
            <div className="space-y-3">
              <div className="text-[10px] uppercase tracking-wider font-semibold text-zinc-500">Center diamond grading</div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="text-[11px] text-zinc-600">Carat</Label>
                  <Input
                    type="number" step="0.01" min="0.1" max="20"
                    value={centerCarat}
                    onChange={(e) => setCenterCarat(e.target.value)}
                    className="h-9 mt-1"
                    data-testid="appraisal-center-carat-input"
                  />
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Shape</Label>
                  <select value={shape} onChange={(e) => setShape(e.target.value)} className="w-full h-9 mt-1 px-2 text-sm border border-zinc-200 rounded bg-white" data-testid="appraisal-shape-select">
                    {SHAPE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Color</Label>
                  <select value={color} onChange={(e) => setColor(e.target.value)} className="w-full h-9 mt-1 px-2 text-sm border border-zinc-200 rounded bg-white">
                    {COLOR_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Clarity</Label>
                  <select value={clarity} onChange={(e) => setClarity(e.target.value)} className="w-full h-9 mt-1 px-2 text-sm border border-zinc-200 rounded bg-white">
                    {CLARITY_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Cut</Label>
                  <select value={cut} onChange={(e) => setCut(e.target.value)} className="w-full h-9 mt-1 px-2 text-sm border border-zinc-200 rounded bg-white">
                    {GRADE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Symmetry</Label>
                  <select value={symmetry} onChange={(e) => setSymmetry(e.target.value)} className="w-full h-9 mt-1 px-2 text-sm border border-zinc-200 rounded bg-white">
                    {GRADE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Polish</Label>
                  <select value={polish} onChange={(e) => setPolish(e.target.value)} className="w-full h-9 mt-1 px-2 text-sm border border-zinc-200 rounded bg-white">
                    {GRADE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-[11px] text-zinc-600">Fluorescence</Label>
                  <select value={fluorescence} onChange={(e) => setFluorescence(e.target.value)} className="w-full h-9 mt-1 px-2 text-sm border border-zinc-200 rounded bg-white">
                    {FLUORESCENCE_OPTIONS.map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </div>
                <div className="col-span-2">
                  <Label className="text-[11px] text-zinc-600">IGI / GIA report number</Label>
                  <Input
                    value={igiNumber}
                    onChange={(e) => setIgiNumber(e.target.value)}
                    placeholder="e.g. LG633457821"
                    className="h-9 mt-1 font-mono"
                    data-testid="appraisal-igi-input"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* breakdown */}
          {preview && (
            <div className="bg-zinc-50/70 border border-zinc-200 rounded-lg overflow-hidden text-xs">
              <div className="px-3 py-2 bg-zinc-100/70 border-b border-zinc-200 text-[10px] uppercase tracking-wider font-semibold text-zinc-600">
                Breakdown
              </div>
              <table className="w-full">
                <tbody>
                  <tr className="border-b border-zinc-100">
                    <td className="px-3 py-1.5 text-zinc-600">
                      Metal {preview.metal_info?.source === 'spot' ? `(spot · ${preview.ring_weight_g}g × $${preview.metal_info?.spot_per_gram?.toFixed(2)}/g × ${(preview.metal_info?.purity || 0).toFixed(4)})` : '(static fallback)'}
                    </td>
                    <td className="px-3 py-1.5 text-right font-mono">{fmtUSD(components.ring_metal)}</td>
                  </tr>
                  <tr className="border-b border-zinc-100"><td className="px-3 py-1.5 text-zinc-600">Center stone</td><td className="px-3 py-1.5 text-right font-mono">{fmtUSD(components.center_stone)}</td></tr>
                  <tr className="border-b border-zinc-100"><td className="px-3 py-1.5 text-zinc-600">Side stones</td><td className="px-3 py-1.5 text-right font-mono">{fmtUSD(components.side_stones)}</td></tr>
                  <tr className="border-b border-zinc-100"><td className="px-3 py-1.5 text-zinc-600">Setting & labor</td><td className="px-3 py-1.5 text-right font-mono">{fmtUSD(components.ring_setting)}</td></tr>
                  <tr className="border-b border-zinc-100"><td className="px-3 py-1.5 text-zinc-600">Profit / margin</td><td className="px-3 py-1.5 text-right font-mono">{fmtUSD(components.ring_profit)}</td></tr>
                  {(components.band_metal || 0) > 0 && (
                    <>
                      <tr className="border-b border-zinc-100"><td className="px-3 py-1.5 text-zinc-600">Band metal</td><td className="px-3 py-1.5 text-right font-mono">{fmtUSD(components.band_metal)}</td></tr>
                      <tr className="border-b border-zinc-100"><td className="px-3 py-1.5 text-zinc-600">Band setting / stones / profit</td><td className="px-3 py-1.5 text-right font-mono">{fmtUSD((components.band_side_stones || 0) + (components.band_setting || 0) + (components.band_profit || 0))}</td></tr>
                    </>
                  )}
                  <tr className="border-b border-zinc-100 bg-zinc-100/50"><td className="px-3 py-1.5 font-medium text-zinc-700">Subtotal</td><td className="px-3 py-1.5 text-right font-mono font-medium">{fmtUSD(preview.subtotal)}</td></tr>
                  <tr className="border-b border-zinc-100"><td className="px-3 py-1.5 text-zinc-600">+ {Math.round((preview.uplift_pct || 0) * 100)}% retail uplift</td><td className="px-3 py-1.5 text-right font-mono">{fmtUSD(preview.uplift)}</td></tr>
                  <tr className="bg-amber-50"><td className="px-3 py-2 font-semibold text-zinc-900">Estimated retail replacement value</td><td className="px-3 py-2 text-right font-mono font-bold text-amber-700">{fmtUSD(preview.total_usd)}</td></tr>
                </tbody>
              </table>
            </div>
          )}

          {/* actions */}
          <div className="flex items-center justify-between flex-wrap gap-3 pt-2 border-t border-zinc-100">
            <Button onClick={save} variant="outline" size="sm" data-testid="appraisal-save-btn">Save inputs</Button>
            <div className="flex items-center gap-2">
              {saved.certificate?.url && (
                <a href={saved.certificate.url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-zinc-200 rounded hover:bg-zinc-50 text-zinc-700"
                  data-testid="appraisal-cert-download">
                  <Download size={12} /> Certificate (1-page)
                </a>
              )}
              {saved.booklet?.url && (
                <a href={saved.booklet.url} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-xs px-3 py-2 border border-zinc-200 rounded hover:bg-zinc-50 text-zinc-700"
                  data-testid="appraisal-booklet-download">
                  <Download size={12} /> Booklet (tri-fold)
                </a>
              )}
              <Button
                onClick={generate}
                disabled={generating}
                className="bg-amber-600 hover:bg-amber-700 text-white"
                data-testid="appraisal-generate-btn"
              >
                {generating
                  ? <Loader2 size={14} className="animate-spin mr-1.5" />
                  : <Sparkles size={14} className="mr-1.5" />}
                {saved.last_generated_at ? 'Regenerate both PDFs' : 'Generate certificate + booklet'}
              </Button>
            </div>
          </div>
          {saved.last_generated_at && (
            <p className="text-[10px] text-zinc-400 italic">
              Last generated {new Date(saved.last_generated_at).toLocaleString()} · saved value {fmtUSD(saved.last_total_usd)} at spot ${saved.last_spot_gold_per_oz?.toLocaleString?.()}/oz
            </p>
          )}
        </>
      )}
    </div>
  );
}
